import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { trackPageView } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => { trackPageView('contact'); }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent!",
          description: response.data.message
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.error || "There was an error sending your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Have a question? We're here to help
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <Card className="contact-form-card">
                <CardHeader>
                  <CardTitle>Send us a message</CardTitle>
                  <p>Fill out the form below and we'll respond within 24 hours</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-field">
                      <label>Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                      />
                    </div>

                    <div className="form-grid">
                      <div className="form-field">
                        <label>Email *</label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                      <div className="form-field">
                        <label>Phone</label>
                        <Input
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="020 1234 5678"
                        />
                      </div>
                    </div>

                    <div className="form-field">
                      <label>Subject *</label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="What's this about?"
                      />
                    </div>

                    <div className="form-field">
                      <label>Message *</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Your message..."
                        rows={5}
                      />
                    </div>

                    <Button type="submit" className="btn-primary btn-large btn-full" disabled={loading}>
                      <Send size={20} />
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="contact-info-wrapper">
              <div className="contact-info-section">
                <h2>Get in Touch</h2>
                <p>
                  Whether you're a homeowner looking for help or an engineer interested in joining, we'd love to hear from you.
                </p>
              </div>

              <div className="contact-methods">
                <Card className="contact-method-card">
                  <CardContent className="contact-method-content">
                    <Mail className="contact-method-icon" size={32} />
                    <h3>Email</h3>
                    <a href="mailto:admin@thegasguide.co.uk">admin@thegasguide.co.uk</a>
                  </CardContent>
                </Card>

                <Card className="contact-method-card">
                  <CardContent className="contact-method-content">
                    <MapPin className="contact-method-icon" size={32} />
                    <h3>Location</h3>
                    <p>Serving all of the UK</p>
                  </CardContent>
                </Card>
              </div>

              <div className="contact-info-section">
                <h3>Office Hours</h3>
                <p>
                  Monday - Friday: 9:00 AM - 6:00 PM<br />
                  Saturday: 10:00 AM - 2:00 PM<br />
                  Sunday: Closed
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
